// Línea temporal del héroe B2, portada línea a línea de renderVals() de Heroe_B2 y Heroe_B2_Movil
// (UI-SPEC §Interaction 1). Es pura: sin React ni DOM, para que el servidor pinte el póster con ella,
// el reloj del cliente la llame en cada fotograma y los tests la comprueben sin navegador.
// Devuelve datos (números, transformaciones redondeadas y una clave de luz por registro), nunca
// colores: la vista traduce cada luz a una clase de token.
import {
    GEOMETRIA_ESCRITORIO,
    GEOMETRIA_MOVIL,
    generarRegistros,
    huecosLibres,
    type GeometriaBiblioteca,
    type RegistroBiblioteca,
} from '@/lib/marketing/geometriaBiblioteca';

import { CONTADOR_HEROE, ESCENARIO_HEROE, PREGUNTAS_HEROE } from './textosHeroe';

export const DURACION_OBRA = 7.4;
export const DURACION_PREGUNTA = 4.6;
export const TOTAL_BUCLE = 30.4;
// Biblioteca completa y primera respuesta con su [S2]: el servidor, el no-JS y el movimiento reducido.
export const FOTOGRAMA_POSTER = 10.8;
// El contador enseña la cifra de escritorio también en móvil, aunque allí se dibujen menos lomos.
const REGISTROS_ANUNCIADOS = 258;

// Qué función lee y cuál escribe cada pregunta (índice de sector, -1 si ninguna). H-1: la pregunta 2
// lee las sesiones, porque el informe de seguimiento se redacta del caso y no de una plantilla.
const ACCIONES = [
    { lee: 0, escribe: -1 },
    { lee: 0, escribe: 2 },
    { lee: 3, escribe: -1 },
    { lee: 5, escribe: -1 },
    { lee: -1, escribe: 6 },
] as const;

export type Punto = readonly [number, number];
export type LuzRegistro = 'barrido' | 'citado' | 'guardado' | null;

export interface EscenaHeroe {
    readonly nombre: 'escritorio' | 'movil';
    readonly geometria: GeometriaBiblioteca;
    readonly centro: Punto;
    readonly salidaAsistente: Punto;
    readonly llegadaAsistente: Punto;
    // Registro que se saca del estante en cada pregunta (el `hueco` de cada tablero).
    readonly huecos: readonly number[];
    readonly maxLetras: number;
    readonly radioSalida: number;
    readonly crecimientoLuz: number;
    readonly crecimientoCita: number;
    readonly separacionEtiqueta: number;
    readonly haloFuncion: number;
    readonly radioPaquete: number;
    readonly tramoVertical: boolean;
    readonly marcador: string;
}

export const ESCENA_ESCRITORIO: EscenaHeroe = {
    nombre: 'escritorio',
    geometria: GEOMETRIA_ESCRITORIO,
    centro: [406, 268],
    salidaAsistente: [458, 268],
    llegadaAsistente: [828, 268],
    huecos: [1, 6, 33, 9, 0],
    maxLetras: 36,
    radioSalida: 46,
    crecimientoLuz: 3,
    crecimientoCita: 9,
    separacionEtiqueta: 18,
    haloFuncion: 6,
    radioPaquete: 5,
    tramoVertical: false,
    marcador: ESCENARIO_HEROE.marcadorEscritorio,
};

export const ESCENA_MOVIL: EscenaHeroe = {
    nombre: 'movil',
    geometria: GEOMETRIA_MOVIL,
    centro: [163, 170],
    salidaAsistente: [163, 202],
    llegadaAsistente: [163, 358],
    huecos: [1, 4, 24, 6, 0],
    maxLetras: 22,
    radioSalida: 27,
    crecimientoLuz: 2,
    crecimientoCita: 6,
    separacionEtiqueta: 12,
    haloFuncion: 5,
    radioPaquete: 4.5,
    tramoVertical: true,
    marcador: ESCENARIO_HEROE.marcadorMovil,
};

export interface RegistroEscena extends RegistroBiblioteca {
    // Segundo del bucle en que el registro empieza a volar a su estante.
    readonly llegada: number;
    // Lo guarda el asistente en el acto 2 (primer hueco libre de su sector).
    readonly nuevo: boolean;
}

export interface EstiloNodo {
    readonly opacidad: number;
    readonly transformacion: string;
}

export interface FotogramaRegistro extends EstiloNodo {
    readonly ancho: number;
    readonly alto: number;
    readonly luz: LuzRegistro;
}

export interface FotogramaFuncion {
    readonly activa: boolean;
    readonly halo: number;
    readonly etiqueta: number;
}

export interface FotogramaHilo extends EstiloNodo {
    readonly ancho: number;
}

export interface FotogramaTramo extends EstiloNodo {
    readonly origen: string;
}

export interface FotogramaTexto extends EstiloNodo {
    readonly texto: string;
}

export interface FotogramaRespuesta extends EstiloNodo {
    readonly visible: boolean;
}

export interface FotogramaHeroe {
    readonly registros: readonly FotogramaRegistro[];
    readonly ondas: readonly EstiloNodo[];
    readonly contador: string;
    readonly funciones: readonly FotogramaFuncion[];
    readonly hilos: readonly FotogramaHilo[];
    readonly tramo: FotogramaTramo;
    readonly etiquetas: readonly FotogramaTexto[];
    readonly compositor: { readonly texto: string; readonly escribiendo: boolean; readonly escalaEnvio: string };
    readonly vacio: number;
    readonly burbuja: FotogramaTexto;
    readonly pensando: { readonly texto: string; readonly opacidad: number; readonly pulso: string };
    readonly avatar: number;
    readonly respuestas: readonly FotogramaRespuesta[];
    readonly haloCita: number;
    readonly chipCita: number;
    readonly notaFija: EstiloNodo;
    readonly paquetes: readonly EstiloNodo[];
}

// ---------- utilidades del tablero ----------

const cl = (x: number) => Math.max(0, Math.min(1, x));
const su = (x: number) => x * x * (3 - 2 * x);
const r2 = (x: number) => Math.round(x * 100) / 100 + 0;

function polar(escena: EscenaHeroe, angulo: number, radio: number): Punto {
    const rad = (angulo * Math.PI) / 180;
    return [escena.centro[0] + radio * Math.cos(rad), escena.centro[1] + radio * Math.sin(rad)];
}

// ---------- registros de la biblioteca ----------

function llegadaFija(registro: RegistroBiblioteca, llenos: readonly number[]): number {
    return 0.3 + 0.5 * Math.floor((registro.indiceEnSector * 12) / llenos[registro.sector]) + registro.azarLlegada * 0.3;
}

function registrosNuevos(geometria: GeometriaBiblioteca): RegistroEscena[] {
    return huecosLibres(geometria).flatMap((hueco) => {
        const pregunta = ACCIONES.findIndex((a) => a.escribe === hueco.sector);
        if (pregunta < 0) return [];
        const llegada = DURACION_OBRA + DURACION_PREGUNTA * pregunta + 3.0;
        return [{ ...hueco, tono: 'accent-blue' as const, llegada, nuevo: true }];
    });
}

const CACHE_REGISTROS = new Map<EscenaHeroe['nombre'], RegistroEscena[]>();

/** Los registros que dibuja el héroe, en el orden del tablero: los compartidos y los dos que guarda. */
export function registrosDeEscena(escena: EscenaHeroe): readonly RegistroEscena[] {
    const guardados = CACHE_REGISTROS.get(escena.nombre);
    if (guardados) return guardados;
    const { geometria } = escena;
    const fijos = generarRegistros(geometria).map((r) => ({ ...r, llegada: llegadaFija(r, geometria.llenos), nuevo: false }));
    const todos = [...fijos, ...registrosNuevos(geometria)].sort(
        (a, b) => a.sector - b.sector || a.indiceEnSector - b.indiceEnSector,
    );
    CACHE_REGISTROS.set(escena.nombre, todos);
    return todos;
}

// ---------- el instante ----------

interface Instante {
    readonly t: number;
    readonly enObra: boolean;
    readonly indice: number;
    readonly s: number;
    readonly lee: number;
    readonly escribe: number;
    readonly hueco: number;
    readonly fuera: number;
    readonly cierre: number;
    readonly k: (desde: number, duracion: number) => number;
}

// El resto se redondea a microsegundos para que t y t + 30,4 caigan en el mismo instante.
function normalizar(t: number): number {
    const resto = ((t % TOTAL_BUCLE) + TOTAL_BUCLE) % TOTAL_BUCLE;
    return Math.round(resto * 1e6) / 1e6;
}

function crearInstante(tBruto: number, escena: EscenaHeroe): Instante {
    const t = normalizar(tBruto);
    const enObra = t < DURACION_OBRA;
    const indice = enObra ? -1 : Math.min(ACCIONES.length - 1, Math.floor((t - DURACION_OBRA) / DURACION_PREGUNTA));
    const s = enObra ? -1 : t - DURACION_OBRA - indice * DURACION_PREGUNTA;
    const k = (desde: number, duracion: number) => (enObra ? 0 : su(cl((s - desde) / duracion)));
    const accion = enObra ? { lee: -1, escribe: -1 } : ACCIONES[indice];
    const cierre = 1 - su(cl((t - (TOTAL_BUCLE - 0.4)) / 0.35));
    const hueco = enObra ? -1 : escena.huecos[indice];
    return { t, enObra, indice, s, ...accion, hueco, fuera: enObra ? 0 : 1 - k(4.25, 0.3), cierre, k };
}

// ---------- registros ----------

interface EstadoRegistros {
    readonly registros: FotogramaRegistro[];
    readonly vistos: number;
    readonly nuevos: number;
    readonly citado: RegistroEscena | null;
    readonly guardado: RegistroEscena | null;
}

const OCULTO: FotogramaRegistro = { ancho: 0, alto: 0, opacidad: 0, transformacion: 'none', luz: null };

function luzDeObra(i: Instante, r: RegistroEscena): boolean {
    const d = (i.t - 6.35) / 0.85 - ((r.angulo - 180) % 360) / 360;
    return d >= 0 && d < 0.07;
}

function luzDeBusqueda(i: Instante, r: RegistroEscena, escena: EscenaHeroe): boolean {
    const d = (i.s - 1.25) / 0.7 - r.indiceEnSector / escena.geometria.llenos[r.sector];
    return d >= 0 && d < 0.14;
}

function transformacionRegistro(escena: EscenaHeroe, r: RegistroEscena, pa: number): string {
    const radio = escena.radioSalida + (r.radio - escena.radioSalida) * (1 - (1 - pa) * (1 - pa));
    const [cx, cy] = escena.centro;
    return `translate(${cx}px,${cy}px) rotate(${r2(r.angulo)}deg) translate(${r2(radio)}px,${-r.grosor / 2}px)`;
}

interface LuzCalculada {
    readonly largo: number;
    readonly luz: LuzRegistro;
    readonly citado: boolean;
    readonly guardado: boolean;
}

function luzDeLaPregunta(i: Instante, r: RegistroEscena, escena: EscenaHeroe): LuzCalculada {
    let largo = r.largo;
    let luz: LuzRegistro = null;
    if (r.nuevo) {
        const guardado = r.sector === i.escribe;
        return { largo, luz: guardado && i.s < 4.25 ? 'guardado' : null, citado: false, guardado };
    }
    if (r.sector !== i.lee) return { largo, luz, citado: false, guardado: false };
    if (luzDeBusqueda(i, r, escena)) [luz, largo] = ['barrido', largo + escena.crecimientoLuz];
    if (r.indiceEnSector !== i.hueco) return { largo, luz, citado: false, guardado: false };
    const c = i.k(1.95, 0.25) * i.fuera;
    return { largo: largo + escena.crecimientoCita * c, luz: c > 0.4 ? 'citado' : luz, citado: true, guardado: false };
}

function luzDelRegistro(i: Instante, r: RegistroEscena, escena: EscenaHeroe): LuzCalculada {
    if (!i.enObra) return luzDeLaPregunta(i, r, escena);
    const barrido = luzDeObra(i, r);
    return { largo: r.largo + (barrido ? escena.crecimientoLuz : 0), luz: barrido ? 'barrido' : null, citado: false, guardado: false };
}

function calcularRegistros(i: Instante, escena: EscenaHeroe): EstadoRegistros {
    const estado = { registros: [] as FotogramaRegistro[], vistos: 0, nuevos: 0, citado: null as RegistroEscena | null, guardado: null as RegistroEscena | null };
    for (const r of registrosDeEscena(escena)) {
        const pa = su(cl((i.t - r.llegada) / 0.5));
        if (pa <= 0) { estado.registros.push(OCULTO); continue; }
        if (r.nuevo) estado.nuevos++; else estado.vistos++;
        const luz = luzDelRegistro(i, r, escena);
        if (luz.citado) estado.citado = r;
        if (luz.guardado) estado.guardado = r;
        const opacidad = r2(Math.min(1, pa * 3) * i.cierre);
        estado.registros.push({ ancho: r2(luz.largo), alto: r.grosor, opacidad, transformacion: transformacionRegistro(escena, r, pa), luz: luz.luz });
    }
    return estado;
}

// ---------- ondas y contador ----------

function sesionActual(t: number): number {
    return Math.floor((t - 0.3) / 0.5);
}

function calcularOndas(i: Instante): EstiloNodo[] {
    const sesion = sesionActual(i.t);
    return [0, 1].map((par) => {
        const n = sesion - (((sesion % 2) + 2) % 2 === par ? 0 : 1);
        const tau = i.t - (0.3 + 0.5 * n);
        const viva = i.enObra && n >= 0 && n < 12 && tau >= 0 && tau <= 0.9;
        return { opacidad: viva ? r2(0.45 * (1 - tau / 0.9)) : 0, transformacion: `scale(${r2(viva ? 0.22 + 0.78 * (tau / 0.9) : 0.2)})` };
    });
}

function textoContador(i: Instante, estado: EstadoRegistros, escena: EscenaHeroe): string {
    const base = escena.geometria.llenos.reduce((a, b) => a + b, 0);
    const cuenta = Math.round((estado.vistos * REGISTROS_ANUNCIADOS) / base) + estado.nuevos;
    if (!i.enObra) return CONTADOR_HEROE.completo(cuenta);
    const sesiones = Math.max(0, Math.min(12, sesionActual(i.t) + 1));
    return sesiones ? CONTADOR_HEROE.enObra(sesiones, cuenta) : ESCENARIO_HEROE.primeraSesion;
}

// ---------- funciones ----------

function calcularFunciones(i: Instante, escena: EscenaHeroe): FotogramaFuncion[] {
    return Array.from({ length: 8 }, (_, j) => {
        const lee = i.lee === j;
        const escribe = i.escribe === j;
        const desde = escribe && i.lee >= 0 ? 2.0 : 0;
        const luz = lee || escribe ? i.k(desde, 0.3) * i.fuera : 0;
        return { activa: luz > 0.5, halo: r2(escena.haloFuncion * luz), etiqueta: r2(luz) };
    });
}

// ---------- hilos, tramo y etiquetas ----------

function hilo(i: Instante, escena: EscenaHeroe, r: RegistroEscena | null, desde: number): FotogramaHilo {
    if (!r) return { ancho: 0, opacidad: 0, transformacion: 'none' };
    const [cx, cy] = escena.centro;
    const p = i.k(desde, 0.35);
    return {
        ancho: r2(r.radio + r.largo / 2),
        opacidad: r2(i.s >= desde ? i.fuera : 0),
        transformacion: `translate(${cx}px,${cy}px) rotate(${r2(r.angulo)}deg) scaleX(${r2(p)})`,
    };
}

function etiqueta(i: Instante, escena: EscenaHeroe, r: RegistroEscena | null, texto: string, desde: number): FotogramaTexto {
    if (!r) return { texto: '', opacidad: 0, transformacion: 'none' };
    const p = i.k(desde, 0.25);
    // Más allá de la punta del registro, hacia fuera, para no taparlo.
    const [x, y] = polar(escena, r.angulo, r.radio + r.largo + escena.separacionEtiqueta);
    const lado = Math.cos((r.angulo * Math.PI) / 180) < 0 ? '-100%' : '0%';
    return { texto, opacidad: r2(p * i.fuera), transformacion: `translate(${r2(x)}px,${r2(y)}px) translate(${lado},-50%) scale(${r2(0.85 + 0.15 * p)})` };
}

// Paciente → asistente: se enciende hacia el asistente al leer y hacia la paciente al guardar.
function calcularTramo(i: Instante, escena: EscenaHeroe): FotogramaTramo {
    const eje = escena.tramoVertical ? 'scaleY' : 'scaleX';
    const leyendo = i.lee >= 0;
    const desde = leyendo ? 1.66 : 2.45;
    const p = leyendo ? i.k(1.66, 0.45) : i.k(2.45, 0.4);
    const origenes = escena.tramoVertical ? ['50% 0', '50% 100%'] : ['0 50%', '100% 50%'];
    return { origen: leyendo ? origenes[0] : origenes[1], opacidad: i.s >= desde ? r2(i.fuera) : 0, transformacion: `${eje}(${r2(p)})` };
}

// ---------- asistente ----------

function calcularCompositor(i: Instante, escena: EscenaHeroe, pregunta: string): FotogramaHeroe['compositor'] {
    const tecleado = pregunta.slice(0, Math.floor(pregunta.length * cl((i.s - 0.2) / 0.8)));
    const escribiendo = i.s >= 0.15 && i.s < 1.1;
    const visto = tecleado.length > escena.maxLetras ? '…' + tecleado.slice(1 - escena.maxLetras) : tecleado;
    const escala = 1 - 0.12 * i.k(1.0, 0.1) * (1 - i.k(1.1, 0.12));
    return { texto: escribiendo ? visto + '|' : escena.marcador, escribiendo, escalaEnvio: `scale(${r2(escala)})` };
}

function calcularRespuestas(i: Instante): FotogramaRespuesta[] {
    const pr = i.k(2.15, 0.35);
    return ACCIONES.map((_, m) => ({
        visible: m === i.indice && i.s >= 2.1,
        opacidad: r2(pr * i.fuera),
        transformacion: `translateY(${r2((1 - pr) * 8)}px)`,
    }));
}

function calcularAsistente(i: Instante, escena: EscenaHeroe) {
    const pregunta = i.enObra ? null : PREGUNTAS_HEROE[i.indice];
    const pb = i.k(1.1, 0.25);
    const pc = i.k(2.55, 0.3);
    return {
        compositor: calcularCompositor(i, escena, pregunta?.pregunta ?? ''),
        vacio: i.enObra ? r2(su(cl((i.t - 0.2) / 0.4))) : 0,
        burbuja: { texto: pregunta?.pregunta ?? '', opacidad: r2(pb * i.fuera), transformacion: `translateY(${r2((1 - pb) * 8)}px)` },
        pensando: { texto: pregunta?.pensando ?? '', opacidad: r2(i.k(1.3, 0.2) * (1 - i.k(2.0, 0.15))), pulso: `scale(${r2(0.85 + 0.15 * Math.sin(i.s * 9))})` },
        avatar: r2(i.k(1.3, 0.2) * i.fuera),
        respuestas: calcularRespuestas(i),
        haloCita: r2(4 * i.k(2.6, 0.3)),
        chipCita: r2(i.k(2.75, 0.3)),
        notaFija: { opacidad: r2(pc), transformacion: `scale(${r2(0.9 + 0.1 * pc)})` },
    };
}

// ---------- paquetes de datos ----------

type Viaje = readonly [number, number, number, number, number, number];

function viajes(escena: EscenaHeroe, citado: RegistroEscena | null, guardado: RegistroEscena | null): Viaje[] {
    const { centro: C, salidaAsistente: A0, llegadaAsistente: A1 } = escena;
    const lista: Viaje[] = [];
    for (const d of [0, 0.12, 0.24]) {
        if (citado) {
            const o = polar(escena, citado.angulo, citado.radio + citado.largo / 2);
            lista.push([o[0], o[1], C[0], C[1], 1.3 + d, 0.42], [A0[0], A0[1], A1[0], A1[1], 1.66 + d, 0.4]);
        }
        if (guardado) {
            const o = polar(escena, guardado.angulo, guardado.radio + guardado.largo / 2);
            lista.push([A1[0], A1[1], A0[0], A0[1], 2.45 + d, 0.36], [C[0], C[1], o[0], o[1], 2.76 + d, 0.42]);
        }
    }
    while (lista.length < 12) lista.push([0, 0, 0, 0, 99, 1]);
    return lista;
}

function calcularPaquetes(i: Instante, escena: EscenaHeroe, estado: EstadoRegistros): EstiloNodo[] {
    const radio = escena.radioPaquete;
    return viajes(escena, estado.citado, estado.guardado).map((w) => {
        const p = su(cl((i.s - w[4]) / w[5]));
        const x = r2(w[0] + (w[2] - w[0]) * p - radio);
        const y = r2(w[1] + (w[3] - w[1]) * p - radio);
        return { opacidad: i.s >= w[4] && i.s <= w[4] + w[5] ? 1 : 0, transformacion: `translate(${x}px,${y}px)` };
    });
}

// ---------- la escena completa ----------

/** El fotograma del héroe en el segundo `t` del bucle (cualquier t: se reduce al bucle de 30,4 s). */
export function escenaHeroe(t: number, escena: EscenaHeroe): FotogramaHeroe {
    const i = crearInstante(t, escena);
    const estado = calcularRegistros(i, escena);
    const pregunta = i.enObra ? null : PREGUNTAS_HEROE[i.indice];
    return {
        registros: estado.registros,
        ondas: calcularOndas(i),
        contador: textoContador(i, estado, escena),
        funciones: calcularFunciones(i, escena),
        hilos: [hilo(i, escena, estado.citado, 1.95), hilo(i, escena, estado.guardado, 2.85)],
        tramo: calcularTramo(i, escena),
        etiquetas: [
            etiqueta(i, escena, estado.citado, pregunta?.etiquetaLee ?? '', 2.05),
            etiqueta(i, escena, estado.guardado, pregunta?.etiquetaEscribe ?? '', 3.15),
        ],
        ...calcularAsistente(i, escena),
        paquetes: calcularPaquetes(i, escena, estado),
    };
}
